import React, { useState, useEffect } from 'react';
import { FileText, File, Trash2, Edit2, BarChart3, Plus, Calendar, Hash, ArrowLeft, Check, X } from 'lucide-react';
import './Documents.css';

const STORAGE_KEY = 'researchWizard_documents';

const DocumentLibrary = ({ onSelectDocument, onUploadNew, onBack }) => {
  const [documents, setDocuments] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        setDocuments(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load documents:', e);
      }
    }
  }, []);

  const saveDocuments = (docs) => {
    setDocuments(docs);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(docs));
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this document? This cannot be undone.')) {
      const updated = documents.filter(d => d.id !== id);
      saveDocuments(updated);
    }
  };

  const handleRename = (id) => {
    const doc = documents.find(d => d.id === id);
    if (doc) {
      setEditingId(id);
      setEditTitle(doc.title);
    }
  };

  const saveRename = () => {
    if (editTitle.trim() && editingId) {
      const updated = documents.map(d => 
        d.id === editingId ? { ...d, title: editTitle.trim() } : d
      );
      saveDocuments(updated);
      setEditingId(null);
      setEditTitle('');
    }
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditTitle('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return <File size={24} className="file-icon pdf" />;
      case 'docx': return <FileText size={24} className="file-icon docx" />;
      case 'xlsx':
      case 'xls': return <FileText size={24} className="file-icon xlsx" />;
      default: return <FileText size={24} className="file-icon txt" />;
    }
  };

  return (
    <div className="document-library">
      <div className="library-header">
        <div className="header-left">
          <button className="btn-back" onClick={onBack}>
            <ArrowLeft size={18} />
            Back
          </button>
          <h2><FileText size={24} /> Document Library</h2>
        </div>
        <button className="btn-primary" onClick={onUploadNew}>
          <Plus size={18} />
          Upload Qualitative Data Document
        </button>
      </div>

      <p className="library-description">
        Upload interview transcripts, focus group notes, or other documents for qualitative analysis
      </p>

      {documents.length === 0 ? (
        <div className="empty-library">
          <FileText size={64} className="empty-icon" />
          <h3>No documents uploaded yet</h3>
          <p>Upload your first document to start analyzing</p>
          <button className="btn-primary-large" onClick={onUploadNew}>
            <Plus size={20} />
            Upload Qualitative Data Document
          </button>
        </div>
      ) : (
        <div className="document-grid">
          {documents.map(doc => (
            <div key={doc.id} className="document-card">
              <div className="card-header">
                {getFileIcon(doc.fileType)}
                <span className="file-type-badge">.{doc.fileType}</span>
              </div>
              
              <div className="card-body">
                {editingId === doc.id ? (
                  <div className="edit-title-row">
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveRename();
                        if (e.key === 'Escape') cancelRename();
                      }}
                    />
                    <button className="btn-icon save" onClick={saveRename}><Check size={16} /></button>
                    <button className="btn-icon cancel" onClick={cancelRename}><X size={16} /></button>
                  </div>
                ) : (
                  <h3 className="doc-title">{doc.title}</h3>
                )}
                
                <div className="doc-meta">
                  <span><Calendar size={14} /> {formatDate(doc.uploadedAt)}</span>
                  <span><Hash size={14} /> {doc.wordCount.toLocaleString()} words</span>
                </div>
                <div className="doc-meta">
                  <span className="file-size">{formatFileSize(doc.fileSize)}</span>
                </div>
              </div>

              <div className="card-actions">
                <button 
                  className="btn-action analyze"
                  onClick={() => onSelectDocument(doc)}
                >
                  <BarChart3 size={16} />
                  Analyze
                </button>
                <button 
                  className="btn-action edit"
                  onClick={() => handleRename(doc.id)}
                >
                  <Edit2 size={16} />
                </button>
                <button 
                  className="btn-action delete"
                  onClick={() => handleDelete(doc.id)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentLibrary;
